import { upgrade_prototype } from './modules/prototype/index.js';
import { React } from './modules/react/index.js';
import { lazy_load } from './modules/lazy-load/index.ts';
import { ModalDialog } from './modules/modal-dialog/index.js';
import { MobileMenu } from './modules/mobile-menu/index.js';
import { in_view } from './modules/in-view/index.js';
import { iconify } from './modules/iconify/index.js';
import { create_resize_observer } from './modules/resize-observer/index.js';
import { md5 } from './modules/md5/index.js';
import { encrypt, decrypt } from './modules/simple-encrypt/index.js';
import { youtube_overlay, youtube_short_overlay, tiktok_overlay, imgbb_overlay } from './modules/overlay/index.js';
import { install_plugin } from './plugins.js';
import { install_user_toolbar } from './modules/user-toolbar/index.js';
import { install_contact_toolbar } from './modules/contact-toolbar/index.js';
import { email_dialog } from './modules/email-dialog/index.js';
import { animated_png } from './modules/animated-png/index.js';
import { icons } from  './icons.js';
import './style.css';

upgrade_prototype();

let doc_title, doc_icon, timer;

export default Graphite = {
    x_components_observer: null,
    widgets: {},
    app: {
        state:{}
    },
    event_handlers: new Map(),
    modules: new Map(),
    import_queue: new Map(),

    // built-in modules

    lazy_load: lazy_load,
    ModalDialog: ModalDialog,
    MobileMenu: MobileMenu,
    in_view: in_view,
    iconify: iconify,
    install_plugin: install_plugin,
    install_user_toolbar: install_user_toolbar,
    install_contact_toolbar: install_contact_toolbar,
    email_dialog: email_dialog,
    animated_png: animated_png,
    resize_observer: create_resize_observer(),
    youtube_overlay: youtube_overlay,
    youtube_short_overlay: youtube_short_overlay,
    tiktok_overlay: tiktok_overlay,
    imgbb_overlay: imgbb_overlay,
    md5: md5,
    active_context_menu: null,

    on: (event, cb) => {
        if (!Graphite.event_handlers.has(event)) {
            Graphite.event_handlers.set(event, cb);
        }
    },

    debounce:(func, ms) => {
      let timeout;

      return function() {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, arguments), ms);
      }
    },

    throttle:(func, wait) => {
        let time = Date.now();
        
        return function(e) {      
            if ((time + wait - Date.now()) < 0) {
                func(e);
                time = Date.now();
            }
        }        
    },

    wait_until: async (condition, polling_interval = 300, timeout = 5000) => {
      return await new Promise(resolve => {
        setTimeout(() => {
          clearInterval(interval);
          resolve(false);
        }, timeout)
        
        const interval = setInterval(() => {
          if (condition()) {
            resolve(true);
            clearInterval(interval);
          }
        }, polling_interval);
      })
    },

    import: (name) => {        
        /* @vite-ignore */ 

        return new Promise(async resolve => {
            if (!Graphite.modules.has(name)) {
                if (!Graphite.import_queue.has(name)) {
                    Graphite.import_queue.set(name, true);    
                    
                    let module = await import(/* @vite-ignore */'./modules/' + name + '/index.js?v=' + Graphite.config.version + '.' + Graphite.config.build);   

                    if (module) {
                        let obj = module[Object.keys(module)[0]];

                        Graphite.modules.set(name, obj);
                        Graphite.import_queue.delete(name);
                        resolve(obj);
                        return;    
                    }

                    resolve(null);
                    Graphite.import_queue.delete(name);
                    return;
                } else {
                    let module_exists = () => {
                        return Graphite.modules.get(name);
                    }

                    let res = await Graphite.wait_until(module_exists);

                    if (res) {
                        resolve(Graphite.modules.get(name));
                        return;
                    } else {
                        resolve(null);
                        return;
                    }
                }
            } else {
                resolve(Graphite.modules.get(name));
                return;
            }

            resolve(null);
        })
    },

    import_component: (name) => {      
        return new Promise(async resolve => {
            if (!customElements.get(name)) {
                if (!Graphite.import_queue.has(name)) {
                    Graphite.import_queue.set(name, true);
                     
                    await import(/* @vite-ignore */ './components/' + name + '/dist/index.js');
                    Graphite.import_queue.delete(name);
                    resolve(customElements.get(name));
                    return;
                } else {
                    let res = await Graphite.wait_until(() => customElements.get(name));

                    if (res) {
                        resolve(customElements.get(name));
                        return;
                    } else {
                        resolve(null);
                        return;
                    }
                }
            } else {
                resolve(customElements.get(name));
                return;
            }

            resolve(null);
        })
    },

    import_widget: (widget) => {
        return new Promise(async resolve => {
            let _widget = widget.replaceAll('-', '_');

            if (!Graphite.widgets[_widget]) {
                if (!Graphite.import_queue.has(_widget)) {
                    Graphite.import_queue.set(_widget, true);
                     
                    let module = await import(/* @vite-ignore */ './widgets/' + widget + '/index.js');

                    if (module) {
                        let fnc = module[Object.keys(module)[0]];

                        if (fnc) {
                            Graphite.widgets[_widget] = fnc;
                            Graphite.import_queue.delete(_widget);
                            resolve(fnc);
                            return;
                        }
                    }

                    Graphite.import_queue.delete(_widget);
                    resove(null);
                    return;
                } else {
                    let res = await Graphite.wait_until(() => Graphite.widgets[_widget]);

                    if (res) {
                        resolve(Graphite.widgets[_widget]);
                        return;
                    } else {
                        resolve(null);
                        return;
                    }
                }
            } else {
                resolve(Graphite.widgets[_widget]);
                return;
            }

            resolve(null);
        })
    },

    import_widget_style(widget) {
        return new Promise(resolve => {
            if (!document.querySelector('link[data-id="' + widget +'"]')) {
                let head  = document.getElementsByTagName('head')[0];
                let link  = document.createElement('link');
                
                let on_load = () => {
                    link.removeEventListener('load', on_load);
                    resolve(true);                    
                }

                link.addEventListener('load', on_load);
                link.setAttribute('data-id', widget);
                link.rel  = 'stylesheet';
                link.type = 'text/css';
                link.href = './widgets/' + widget + '/style.css';
                head.appendChild(link);
            } else {
                resolve(true);
            }        
        })
    },

    init_widget(widget, options = {}, lazy_load = true) {
        let _widget = document.querySelector('wx-widget[data-id="' + widget.getAttribute('data-id') + '"]');

        options.widget = widget;

        function do_init() {
            let widget_name = widget.getAttribute('data-widget');
            let fnc_name = widget_name.replaceAll('-', '_');

            Graphite.import_widget(widget_name).then(() => {
                Graphite.import_widget_style(widget_name).then(() => {
                    if (Graphite.is_login()) {
                        widget.notify_page_changed = function() {                            
                            let page = this.closest('[data-widget="wx-page"]');

                            if (page) {
                                page.trigger('page-changed');
                            }
                        }

                        widget.install_toolbar_icon = function(icon, context_menu, action) {
                            let block = this.closest('wx-item') || this.closest('wx-block');

                            if (block) {
                                switch (icon) {
                                    case 'setting': {
                                            let toolbar = block.querySelector('& > [data-role="toolbar"]');
                                            let setting = document.createElement('span');

                                            if (toolbar) {
                                                setting.classList.add('settings');
                                                setting.setAttribute('data-tippy', 'Settings');
                                                setting.innerHTML = Graphite.icons['setting'];

                                                if (context_menu) {
                                                    context_menu.mount(setting);
                                                }

                                                if (Graphite.tippy) {
                                                    Graphite.tippy.mount(setting, setting.getAttribute('data-tippy'))
                                                }

                                                if (typeof action == 'function') {
                                                    setting.addEventListener('click', action);
                                                }

                                                toolbar.append(setting);
                                            }
                                        }

                                        break;

                                    case 'edit': {
                                            let toolbar = block.querySelector('& > [data-role="toolbar"]');
                                            let edit = document.createElement('span');

                                            if (toolbar) {
                                                edit.classList.add('edit');
                                                edit.setAttribute('data-tippy', 'Edit');
                                                edit.innerHTML = Graphite.icons['edit'];

                                                if (context_menu) {
                                                    context_menu.mount(edit);
                                                }

                                                if (Graphite.tippy) {
                                                    Graphite.tippy.mount(edit, edit.getAttribute('data-tippy'))
                                                }

                                                if (typeof action == 'function') {
                                                    edit.addEventListener('click', action);
                                                }

                                                toolbar.append(edit);
                                            }
                                        }

                                        break;
                                }
                            }
                        }

                        widget.is_cached = function() {
                            return new Promise(resolve => {
                                let cache_id = this.getAttribute('data-widget') + '-' + this.getAttribute('data-id');

                                Graphite.get_json('./process/cache-exists.php?key=' + cache_id).then(res => {
                                    if (res.status == 200) {
                                        resolve(true);
                                        return;
                                    }

                                    resolve(false);
                                })
                                // if (document.querySelector('[data-cache-id="' + cache_id + '"]')) return true;
                                // return false;
                            })
                        }
                    }

                    Graphite.widgets[fnc_name](options);
                })
            })
        }

        if (lazy_load) {
            Graphite.in_view(widget, () => {
                do_init();
            })
        } else {
            do_init();
        }
    },

    default: (x, y) => {
        if ((x === undefined) || (x === null) || (x === '') || (x === 'undefined')) {
            return y;
        }

        return x;
    },

    pushState: (state = null, url) => {
        window.history.replaceState(state, null, '');
        window.history.pushState(null, null, url);        
    },

    get_theme: () => {
        return Graphite.default(Graphite.app.state.theme, 'light');
    },

    set_theme: (theme, broadcast = true) => {
        localStorage.setItem('theme', theme);

        Graphite.app.state.theme = theme;
        document.documentElement.setAttribute('data-theme', theme); 
        document.trigger('theme-changed');                

        if (broadcast) {
            Graphite.bc.postMessage({
                topic:'state-changed',
                state:Graphite.app.state
            })
        }
    },

    toggle_theme: () => {
        let theme = Graphite.app.state.theme == 'dark'?'light':'dark';

        Graphite.set_theme(theme);
    },

    get_mode: () => {
        return Graphite.default(Graphite.app.state.theme, 'live');
    },

    set_mode: (mode, broadcast = true) => {
        localStorage.setItem('mode', mode);

        Graphite.app.state.mode = mode;
        document.documentElement.setAttribute('data-mode', mode); 
        document.trigger('mode-changed');

        if (broadcast) {
            Graphite.bc.postMessage({
                topic:'state-changed',
                state:Graphite.app.state
            })
        }
    },

    toggle_mode: (mode) => {
        Graphite.app.state.mode == 'live'?'edit':'live';        

        Graphite.bc.postMessage({
            topic:'state-changed',
            state:Graphite.app.state
        })
    },

    authen(user, password) {
        let data = new FormData();

        data.append('user', user);
        data.append('password', Graphite.encrypt(password));

        return Graphite.post('./process/authen.php', data);
    },

    _login: (user, password) => {
        return Graphite.authen(user, password).then(res => {
            if (res.status == 200) {
                localStorage.setItem('login', 'true');
                localStorage.setItem('mode', 'edit');

                setTimeout(() => {
                    location.reload();
                }, 500);     

                return true;             
            } else {
                return false;
            }
        })
    },

    login: () => {
        localStorage.setItem('login', 'true');
        localStorage.setItem('mode', 'edit');

        setTimeout(() => {
            location.href = Graphite.config.www_root + 'home';
        }, 500)     
    },

    logout: () => {
        Graphite.get_json('./process/logout.php').then(res => {
            if (res.status == 200) {
                localStorage.setItem('login', 'false');
                localStorage.setItem('mode', 'live');

                setTimeout(() => {
                    location.reload();
                }, 500)
            }
        })
    },

    is_login: () => {
        if (Graphite.app.state.user) {
            return true;
        }

        return false;
    },

    is_edit_mode: () => {
        return Graphite.app.state.mode == 'edit';
    },

    ready:() => {
        Graphite.state.set('ready', true);
        document.trigger('graphite-ready');
    },

    hash: (message) => {
        function hash(str) {
            let hash = 0, i, chr;
            
            if (str.length === 0) return hash;
            
            for (i = 0; i < str.length; i++) {
                chr = str.charCodeAt(i);
                hash = ((hash << 5) - hash) + chr;
                hash |= 0;
            }

            return hash;
        }

        if  (typeof message == 'object') {
            message = JSON.stringify(message);
        }

        return hash(message);
    },

    encrypt: (s) => {
        return encrypt(s);
    },

    decrypt: (s) => {
        return decrypt(s);
    },

    create_reactive_state: function(state, cb) {
        return new React(state, cb);
    },

    create_timer: (timeout = 4000, cb) => {
        let timer;

        return (run_immediately = false) => {
            clearTimeout(timer);

            if (run_immediately) {
                if (typeof cb == 'function') {
                    cb();
                }                
            } else {
                timer = setTimeout(() => {
                    if (typeof cb == 'function') {
                        cb();
                    }
                }, timeout)
            }                                    
        }
    },

    post: (url, data = null, headers = {}) => new Promise((resolve, reject) => {
        let options = {
            method: 'POST',
            body: data,
            headers: headers
        }

        fetch(url, options)
            .then(response => response.json())
            .then(json => resolve(json))
            .catch(err => reject(err));        
    }),

    post_json: (url, data, headers = {}) => new Promise((resolve, reject) => {
        let _headers = {
            'Content-Type': 'application/json; charset=UTF-8'
        }

        Object.keys(headers).forEach(key => {
            _headers[key] = headers[key]
        })

        const fetch_data = {
          method: 'POST',
          headers: _headers,
          body: JSON.stringify(data)
        }

        fetch(url, fetch_data)
          .then(response => response.json())
          .then(json => resolve(json))
          .catch(err => reject(err));
    }),

    get_json: (url, cached = true) => new Promise((resolve, reject) => {
        let options = {};

        if (!cached) {
            options = {cache: "no-cache"}
        }

        fetch(url, options)
            .then(response => resolve(response.json()))
            .then(json => resolve(json))
            .catch(err => reject(err));
    }),

    get_html: (url, cached = true) => new Promise((resolve, reject) => {
        let options = {};

        if (!cached) {
            options = {cache: "no-cache"}
        }

        fetch(url, options)
            .then(response => response.text())
            .then(text => resolve(text))
            .catch(err => reject(err));
    }),

    get_object: (id, cached = true) => new Promise((resolve, reject) => {
        let options = {};
        let url = Graphite.config.www_root + 'process/get-object.php?id=' + id;

        if (!cached) {
            options = {cache: "no-cache"}
        }

        fetch(url, options)
            .then(response => response.json())
            .then(json => resolve(json))
            .catch(err => reject(err));
    }),

    load_config_and_state: (url) => new Promise(resolve => {
        Graphite.get_json(url).then(res => {
            Graphite.config = res.config;
            Graphite.app.state = res.state;
            Graphite.app.state.theme = Graphite.default(localStorage.getItem('theme'), Graphite.default(Graphite.config.default_theme, 'light'));
            Graphite.app.state.mode = Graphite.default(localStorage.getItem('mode'), 'live');
            Graphite.set_theme(Graphite.app.state.theme, false);

            if (!Graphite.app.state.user) {
                Graphite.app.state.mode = 'live';
                localStorage.setItem('mode', 'live');
            } else {
                Graphite.set_mode(Graphite.app.state.mode, false);
            }

            let format = document.documentElement.getAttribute('data-format');

            if (format == 'print') {
                document.documentElement.attr('data-theme', 'light');
            } else {
                document.documentElement.attr('data-theme', Graphite.app.state.theme);
            }

            document.documentElement.attr('data-mode', Graphite.app.state.mode);

            let reload_from_previous_session = sessionStorage.getItem('reload-from-previous-session');

            if (reload_from_previous_session) {
                sessionStorage.removeItem('reload-from-previous-session');

            } else {
                Graphite.bc.postMessage({
                    topic: 'state-changed',
                    state: Graphite.app.state
                })
            }

            if (Graphite.config.device_pixel_ratio > 1) {
                Graphite.config.hd_display = true;
            }

            resolve();
        })
    }),

    get_time: () => {
        return (new Date()).getTime();
    },

    get_ending: () => {
        let ending = 'ครับ';

        if (Graphite.config.admin.gender == 'female') {
            ending = 'ค่ะ';
        }

        return ending;
    },

    show_message: (str) => {
        let dlg = new ModalDialog({
            id:"message-dlg",
            width: 440,
            padding: '40px 40px 30px'
        })

        let sb = new Array();

        sb.push('<div class="flex-row gap-2">');
        sb.push('<img src="' + Graphite.config.admin.profile_image + '" class="rounded-1 object-fit-cover rounded" width="64" height="64">');
        sb.push('<p>' + str + '</p>');
        sb.push('</div>');
        sb.push('<div class="flex-row justify-center mt-4"><button data-default-button="true" data-cmd="close" data-role="button" class="cursor-pointer">' + Graphite.icons['close'] + '  Close</button></div>');
 
        dlg.html(sb.join(''));

        return dlg.open();
    },

    show_loading: (str, animation) => {
        let dlg = new ModalDialog({
            id:"loading-dlg",
            width: 440,
            padding: '40px 40px 30px',
            close_when_click_outside: false,
            close_with_esc: false
        })

        let sb = new Array();

        sb.push('<div class="flex-col items-center">');
        sb.push('<div class="animated-png" style="--width:220px;--aspect-ratio:4 / 3"></div>');
        sb.push('<p>' + str + '</p>');
        sb.push('<p>please wait...</p>');
        sb.push('</div>');
        dlg.html(sb.join(''));

        let el = dlg.popup.querySelector('.animated-png');

        Graphite.animated_png(el, animation);

        dlg.on('before-open', () => {
            dlg.popup.querySelector('span.ux-icon-close').remove();
        })

        dlg.open();
        return dlg;
    },

    confirm: (str) => {
        let dlg = new ModalDialog({
            id:"confirm-dlg",
            width: 440,
            padding: '40px 40px 30px'
        })

        let sb = new Array();

        sb.push('<div class="flex-row gap-2">');
        sb.push('   <img src="' + Graphite.config.admin.profile_image + '" class="rounded-1 object-fit-cover rounded" width="64" height="64">');
        sb.push('   <p>' + str + '</p>');
        sb.push('</div>');
        sb.push('<footer class="flex-row justify-center mt-4 gap-1.5">');
        sb.push('   <button data-default-button="true" data-cmd="ok" data-role="button" class="cursor-pointer">' + Graphite.icons['ok'] + ' OK</button>');
        sb.push('   <button data-cmd="close" data-role="button" class="cursor-pointer">' + Graphite.icons['close'] + ' Cancel</button>');
        sb.push('</footer>');
 
        dlg.html(sb.join(''));

        dlg.popup.on('click', evt => {
            let el = evt.target;

            if (el.matches('[data-cmd="ok"]')) {
                dlg.set_cmd('ok');
                dlg.set_value('');
                dlg.close();
            }
        })

        return dlg.open();
    },

    popup: (id) => {        
        let dlg = new Graphite.ModalDialog({
            id:"page-dialog",
            page_id: id,
            width: 850,
            padding: '40px 40px 30px'
        })

        let url = Graphite.config.www_root + 'process/get-widget.php?id=' + id + '&widget=wx-page';

        Graphite.get_html(url).then(html => {
            dlg.html(html + '<footer class="flex-row justify-center mt-4 mb-1"><button class="cursor-pointer" data-role="button" data-cmd="close">' + Graphite.icons['close'] + ' Close</button></footer>');
            return dlg.open();
        }).catch(() => {
            let msg = 'ไม่สามารถดึงข้อมูลมาแสดงผลได้ กรุณาติดต่อ Admin เพื่อขอรับความช่วยเหลือ';

            Graphite.show_message(msg);
        })
    },

    cache: {
        set: (key, value) => {
            localStorage.setItem('cache.' + key, value);
        },

        get: (key) => {
            return localStorage.getItem('cache.' + key);
        },

        clear: () => {
            for (let i=0;i<localStorage.length;i++) {
                let key = localStorage.key(i);

                if (key.startsWith('cache.')) {
                    localStorage.removeItem(key);
                }
            }
        }
    },

    get_page_context: id => {
        return new Promise(resolve => {
            let context = Graphite.cache.get('page.context.' + id);

            if (context) {
                resolve(context);
                return;
            }

            let url = Graphite.config.www_root + 'data/' + id + '.json';
            
            Graphite.get_json(url).then(node => {
                Graphite.cache.set('page.context.' + id, Graphite.default(node.context, 'md'));
                resolve(Graphite.default(node.context, 'md'))
            })            
        })
    },

    navigate: (url, target) => {
        let pattern, matches;

        if (url == '#') return;

        if (url.startsWith('tel:')) {
            if (!Graphite.is_mobile_browser()) {
                let v_url = new URL(url);
                Graphite.show_qr('', url, '<i class="icon-phone"></i> ' + v_url.pathname, 'สแกน QR เพื่อโทร');
            } else {
                let new_win = window.open(url);  

                setTimeout(() => {
                    new_win.close();
                }, 100)
            }   
        } else if (url.startsWith('https://m.me/')) {
            window.open(url, '_blank').focus();                
        } else if (url.includes('//line.me/R/ti/p/') || url.includes('//line.me/ti/') || url.includes('//lin.ee/')) {
            if (!Graphite.is_mobile_browser()) {
                Graphite.show_qr('', url, '<i class="icon-line" style="--icon-color:#089609"></i> สแกน QR เพื่อ Chat');
            } else {
                let new_win = window.open(url);  

                setTimeout(() => {
                    new_win.close();
                }, 100)
            }
        } else if (url.startsWith('mailto:')) {
            let v_url = new URL(url);            
            Graphite.email_dialog(v_url.pathname);
        } else if (url.startsWith(Graphite.config.www_root) || !url.startsWith('http')) {
            pattern = /(page|article|news|product)\/(\d+)$/;
            matches = url.match(pattern);

            if (matches) {
                let type = matches[1];
                let id = matches[2];                
                let wx_page = document.querySelector('wx-widget[id="widget_' + id + '"][data-widget="wx-page"]');

                if (wx_page) return;

                if (type == 'page') {
                    Graphite.get_page_context(id).then(context => {
                        if (context == 'md') {
                            Graphite.popup(id);                
                        } else {
                            if (Graphite.state.get('ctrlKey')) {
                                window.open(url, '_blank').focus();
                            } else {
                                location.href = url;
                            }
                        }                    
                    })
                } else {
                    Graphite.popup(id);                                    
                }
            } else {
                if (Graphite.state.get('ctrlKey')) {
                    window.open(url, '_blank').focus();
                } else {
                    if (target == '_blank') {
                        window.open(url, '_blank').focus();
                    } else {
                        window.location = url;
                    }
                }
            }
        } else if (url.includes('//www.youtube.com/watch?v=')) { 
            if (Graphite.has_permission('#overlay#')) {
                Graphite.youtube_overlay(url);
            } else {
                window.open(url, '_blank').focus();
            }
        } else if (url.includes('//youtube.com/shorts/') || url.includes('//www.youtube.com/shorts/')) {
            if (Graphite.has_permission('#overlay#')) {
                Graphite.youtube_short_overlay(url);
            } else {
                window.open(url, '_blank').focus();
            }
        } else if (url.includes('//www.tiktok.com/@')) {
            if (Graphite.has_permission('#overlay#')) {
                pattern = /^https:\/\/www\.tiktok\.com\/@(.+)\/video\/\d+/;

                if (url.match(pattern)) {
                    Graphite.tiktok_overlay(url);
                } else {
                    window.open(url, '_blank').focus();                    
                }                
            } else {
                window.open(url, '_blank').focus();
            }
        } else if (url.includes('//ibb.co/')) {
            Graphite.imgbb_overlay(url);
        } else {
            window.open(url, target).focus();
        }
    },

    has_permission: (permission) => {
        return Graphite.config.permissions.includes(permission);
    },

    apply_icon: (el) => {
        let icon = el.getAttribute('data-icon');

        if (icon) {
            el.html(Graphite.icons[icon] + el.html());
        }
    },

    get_target: (url) => {
        return '_blank';
    },

    get_meta_data: (id) => {
        return Graphite.get_json(Graphite.config.www_root + 'process/get-meta-data.php?id=' + id);
    },

    el: (html) => {
        let div;        
       
        html = html.trim();

        if (html.startsWith('<tr')) {
            div = document.createElement('tbody');
        } else {
            div = document.createElement('div');
        }

        div.innerHTML = html;

        if (div.childElementCount == 1) {
            return div.firstChild;      
        } else if (div.childElementCount > 1) {
            return div.querySelectorAll('& > *');
        }

        return null;
    },

    increase_views: (page_id) => {
        return Graphite.get_json('./process/increase-views.php?id=' + page_id);
    },

    get_uid: () => {
        return (new Date()).getTime().toString(32) + Math.round(Math.random() * 1000).toString(32);
    },

    calculate_discount: (origin_price, selling_price) => {
        return Math.round( 0 - ((origin_price - selling_price) * 100 / origin_price)) + '%';
    },

    begin_saving: () => {
        let el = document.querySelector('link[rel="icon"]');

        doc_title = document.title;
        doc_icon = el?.href ?? '';

        document.title = 'saving...';
        
        if (el) {
            el.href = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNNSAyMWgxNGEyIDIgMCAwIDAgMi0yVjhhMSAxIDAgMCAwLS4yOS0uNzFsLTQtNEExIDEgMCAwIDAgMTYgM0g1YTIgMiAwIDAgMC0yIDJ2MTRhMiAyIDAgMCAwIDIgMnptMTAtMkg5di01aDZ6TTEzIDdoLTJWNWgyek01IDVoMnY0aDhWNWguNTlMMTkgOC40MVYxOWgtMnYtNWEyIDIgMCAwIDAtMi0ySDlhMiAyIDAgMCAwLTIgMnY1SDV6IiBmaWxsPSIjYWFhIi8+PC9zdmc+';
        }


        let title = 'saving';

        timer = setInterval(() => {
            document.title = title;

            title = title + '.';

            if (title == 'saving....') {
                title = 'saving';
            }
        }, 500)
    },

    end_saving: () => {
        if (timer) {
            clearTimeout(timer);
        }

        let el = document.querySelector('link[rel="icon"]');

        document.title = doc_title;

        if (el) {
            el.href = doc_icon;
        }
    },

    number_format: (number) => {
        number = parseInt(number);
        return (number).toLocaleString(undefined, {minimumFractionDigits:0});
    },

    friendly_filesize: (filesize) => {
        if (filesize.length < 7) return `${Math.round(+filesize/1024).toFixed(0)}kb`;
        return `${(Math.round(+filesize/1024)/1000).toFixed(2)}MB`;
    },

    create_int_input: (el) => {
        el.on('keydown', evt => {
            if (['Tab','Backspace','ArrowLeft','ArrowRight','ArrowDown', 'ArrowUp', 'Backspace', 'CapsLock'].includes(evt.key)) return;

            if (evt.ctrlKey && ['c','v','x']) return;

            if (!evt.key.match(/[0-9]/)) {
                evt.preventDefault();
                return;
            }

            let text = el.value + evt.key;
            let ok = false;

            if (text.match(/^\d+$/)) {
                ok = true;
            }

            if (!ok) {
                evt.preventDefault();
            }
        })
    },

    create_float_input: (el) => {
        el.on('keydown', evt => {
            if (['Tab','Backspace','ArrowLeft','ArrowRight','ArrowDown', 'ArrowUp', 'Backspace', 'CapsLock'].includes(evt.key)) return;

            if (evt.ctrlKey && ['c','v','x']) return;

            if (!evt.key.match(/[0-9A-Za-z\.]/)) {
                evt.preventDefault();
                return;
            }

            let text = el.value + evt.key;
            let ok = false;

            if (text.match(/^\d+$/)) {
                ok = true;
            } else if (text.match(/^\d+\.$/)) {
                ok = true;
            } else if (text.match(/^\d+\.\d+$/)) {
                ok = true;
            }

            if (!ok) {
                evt.preventDefault();
            }
        })
    },

    create_discount_input: (el) => {
        el.on('keydown', evt => {
            if (['Tab','Backspace','ArrowLeft','ArrowRight','ArrowDown', 'ArrowUp', 'Backspace', 'CapsLock'].includes(evt.key)) return;

            if (evt.ctrlKey && ['c','v','x']) return;

            if (!evt.key.match(/[0-9%]/)) {
                evt.preventDefault();
                return;
            }

            let text = el.value + evt.key;
            let ok = false;

            if (text.match(/^[0-9_]+%?$/)) {
                ok = true;
            }

            if (!ok) {
                evt.preventDefault();
            }
        })            
    },

    create_email_input: (el) => {
        el.on('keydown', evt => {
            if (['Tab','Backspace','ArrowLeft','ArrowRight','ArrowDown', 'ArrowUp', 'Backspace', 'CapsLock'].includes(evt.key)) return;

            if (evt.ctrlKey && ['c','v','x']) return;

            if (!evt.key.match(/[0-9A-Za-z@_\-\.]/)) {
                evt.preventDefault();
                return;
            }

            let text = el.value + evt.key;
            let ok = false;

            if (text.match(/^([a-zA-Z0-9_]+\.?)+$/)) {
                ok = true;
            } else if (text.match(/^([a-zA-Z0-9_]+\.?)+@$/)) {
                ok = true;
            } else if (text.match(/^([a-zA-Z0-9_]+\.?)+@([a-zA-Z0-9_]+\.?)+$/)) {
                ok = true;
            } else if (text.match(/^([a-zA-Z0-9_]+\.?)+@([a-zA-Z0-9_]+(\.?[a-zA-Z])+)+$/)) {
                ok = true;
            }

            if (!ok) {
                evt.preventDefault();
            }
        })            
    },

    create_tel_input: (el) => {
        el.on('keydown', evt => {
            if (['Tab','Backspace','ArrowLeft','ArrowRight','ArrowDown', 'ArrowUp', 'Backspace', 'CapsLock'].includes(evt.key)) return;

            if (evt.ctrlKey && ['c','v','x']) return;

            if (!evt.key.match(/[0-9\+\-]/)) {
                evt.preventDefault();
                return;
            }

            if (el.value.length > 14) {
                evt.preventDefault();
                return;
            }

            let text = el.value + evt.key;
            let ok = false;

            if (text.match(/^\+$/)) {
                ok = true;
            } else if (text.match(/^\+?([0-9]+\-?)+$/)) { 
                ok = true;
            }

            if (!ok) {
                evt.preventDefault();
            }
        })            
    },

    validate_contact_no: (str) => {
        if (str.match(/^\+?[0-9]+(\-?[0-9]+)+$/)) return true;
        return false;
    },

    validate_email: (str) => {
        if (str.match(/^([0-9a-zA-Z_]+)(\.[0-9a-zA-Z_]+)*@([a-zA-Z]+)(\.[a-zA-Z]+)+$/)) return true;
        return false;
    },

    extract_number: (str) => {
        try {
            let arr = str.matchAll(/[\-\d\.]/g).toArray();
            let minus = false;

            if (arr.length) {
                if (arr[0] == '-') {
                    minus = true;
                }

                arr = arr.filter(item => {
                    return item != '-';
                })

                return (minus?'-':'') + arr.join('');
            }

            return '';
        } catch (err) {
            return '';
        }
    },

    get_qr_payload: (type, id, amount, ref = '') => {
        return Graphite.get_json(Graphite.config.www_root + 'process/get-qr-payload.php?type=' + type + '&id=' + id + '&amount=' + amount + '&ref=' + ref)
    },

    is_mobile_browser: () => {
        if (
            navigator.userAgent.match(/Android/i) ||
            navigator.userAgent.match(/webOS/i) ||
            navigator.userAgent.match(/iPhone/i) ||
            navigator.userAgent.match(/iPad/i) ||
            navigator.userAgent.match(/iPod/i) ||
            navigator.userAgent.match(/BlackBerry/i) ||
            navigator.userAgent.match(/Windows Phone/i)
        ) {
            return true;
        } else {
            return false;
        }
    },

    get_facebook_chat_url: () => {
        return 'https://m.me/' + Graphite.config.facebook_id;
    },

    get_line_chat_url: () => {
        return 'https://line.me/R/ti/p/' + encodeURIComponent(Graphite.config.line_id);
    },

    get_line_oa_chat_url: (text = '') => {
        return 'https://line.me/R/oMessage/' + encodeURIComponent(Graphite.config.line_id) + '/?' + text;        
    },

    open_new_window: (url, width = 820, height = 640, auto_close = false) => {
        let win = window.open(url, '_blank', 'toolbar=no, location=no, status=no, menubar=no, scrollbars=yes, resizable=yes, width=' + width + ', height=' + height);

        if (window.focus) win.focus();

        if (auto_close) {
            win.close();
        }
    },

    show_qr: (header, url, title, sub_title) => {
        let dlg = new ModalDialog({
            id:"qr-code",
            width: 440,
            padding: '60px 40px 30px'
        })

        let sb = new Array();

        sb.push('<div class="flex-col items-center gap-1">');

        if (header) {
            sb.push('<h3>' + header + '</h3>');

        }
        sb.push('   <div class="qr"></div>');
        sb.push('   <div class="info">');
        sb.push('   <p clcass="title">' + title + '</p>');
        
        if (sub_title) {
            sb.push('   <p class="sub-title">' + sub_title + '</p>');
        }
        
        sb.push('   </div>');
        sb.push('</div>');
        sb.push('<div class="flex-row justify-center mt-4"><button data-cmd="close" data-role="button" class="cursor-pointer"><i class="icon-close"></i>  Close</button></div>');

        dlg.html(sb.join(''));

        Graphite.qrcode(dlg.popup.querySelector('.qr'), {
            text: url,
            width: 180,
            height: 180
        })

        return dlg.open();
    },

    clear_cache: (key) => {
        return Graphite.get_json('./process/clear-cache.php?key=' + key);
    },

    encode_extra: (str) => {
        return encodeURIComponent(str);
    },

    get_imgbb_image_info: (url) => {
        return Graphite.get_json('./process/get-imgbb-image-info.php?url=' + encodeURIComponent(url));
    },

    copy_text: str => {
        let input = Graphite.el('<input type="text" class="hidden" value="' + str + '" />');
        document.body.append(input);
        input.select();
        navigator.clipboard.writeText(input.value);
        input.remove();
    },

    reload_custom_style: () => {
        Graphite.get_html('./process/get-custom-style.php').then(css => {
            let el = document.querySelector('[data-id="custom-style');

            if (el) {
                el.html(css);
            }
        })
    },

    select_product: (node) => {
        return new Promise(resolve => {
            let dlg = new Graphite.ModalDialog({
                id:"select-product-dialog",
                width: 720,
                padding: '50px 40px 30px'
            })

            dlg.popup.on('click', evt => {
                let el = evt.target;

                if (el.matches('li')) {
                    evt.preventDefault();
                    evt.stopPropagation();

                    let id = el.getAttribute('data-id');

                    dlg.set_cmd('ok');
                    dlg.set_value(id);
                    dlg.close();
                }
            })

            let sb = new Array();

            sb.push('<div>');
            sb.push('<ul>');
            sb.push('<% prices.forEach(item => { %>')
            sb.push('   <%');
            sb.push('   let selling_price = item.price;');
            sb.push('   let origin_price = "";');
            sb.push('   if (item.promotion_price){');
            sb.push('       selling_price = item.promotion_price;');
            sb.push('       origin_price = item.price;');
            sb.push('   }');
            sb.push('   %>')
            sb.push('   <li data-id="<%= item.id %>">');
            sb.push('   <%');
            sb.push('       let v_image = image.href;');
            sb.push('       let v_image_width = image.width;');
            sb.push('       let v_image_height = image.height;');
            sb.push('       let layout = Graphite.config.products_layout;');
            sb.push('       let product_image_ratio = Graphite.config.product_image_ratio;');
            sb.push('       let aspect_ratio = "1 / 1";');
            sb.push('       if (v_image){');
            sb.push('           v_image = "./resize?href=" + v_image + "&w=120";');
            sb.push('           if (layout == "grid"){');
            sb.push('               aspect_ratio = product_image_ratio;');
            sb.push('           } else {');
            sb.push('               aspect_ratio = v_image_width + " / " + v_image_height');
            sb.push('           }');
            sb.push('       }');
            sb.push('       if (item.image.href){');
            sb.push('           v_image = "./resize?href=" + item.image.href + "&w=120";');
            sb.push('           if (layout == "grid"){');
            sb.push('               aspect_ratio = product_image_ratio;');
            sb.push('           } else {');
            sb.push('               aspect_ratio = item.image.width + " / " + item.image.height;');
            sb.push('           }');
            sb.push('       }');
            sb.push('   %>');
            sb.push('       <mx-placeholder style="--aspect-ratio:<%= aspect_ratio %>;--loading-icon-width:14px">');
            sb.push('           <img class="lazy" src="" data-src="<%= v_image %>" alt="<%= v_image.alt %>" />');
            sb.push('       </mx-placeholder>');
            sb.push('       <div class="col-title">');
            sb.push('           <span class="title"><%= title %></span>');
            sb.push('           <% if (sub_title){ %>');
            sb.push('           <span class="sub-title"><%= sub_title %></span>');
            sb.push('           <% } %>');
            sb.push('           <span class="sku"><%= item.sku %></span>');
            sb.push('       </div>');
            sb.push('       <div class="col-price">');
            sb.push('           <div class="row-selling-price"><span class="selling-price"><%= selling_price %></span> <span class="currency-unit"><%= Graphite.config.currency_unit %></span></div>');
            sb.push('          <% if (origin_price){ %>');
            sb.push('           <div class="row-origin-price"><span class="origin-price"><%= origin_price %></span> <span class="currency-unit"><%= Graphite.config.currency_unit %></span> <span class="discount">(-15%)</span></div>');
            sb.push("           <% } %>");
            sb.push('       </div>');
            sb.push('   </li>');
            sb.push('<% }) %>')
            sb.push('</ul>'); 
            sb.push('<footer>');   
            sb.push('   <button data-role="button" data-cmd="close"><i class="icon-close"></i> CLOSE</button>');   
            sb.push('</footer>');   
            sb.push('</div>');

            let html = Graphite.ejs.render(sb.join(''), node);

            dlg.html(html)    
            
            dlg.popup.querySelectorAll('img.lazy').forEach(img => {
                Graphite.lazy_load(img);
            })    

            resolve(dlg.open());
        })
    },

    buy_now: (id) => {    
        return new Promise(resolve => {
            let dlg = Graphite.ModalDialog.get_active_dialog();

            if (dlg) {
                if (dlg.options.id == 'page-dialog') {
                    dlg.close();
                }
            }

            Graphite.get_json('./data/' + id + '.json').then(async node => {
                let item;

                if (node.prices.length > 1) {
                    let res = await Graphite.select_product(node);

                    if (res.cmd == 'ok') {
                        let id = res.value;
                        let price_map = new Map();

                        node.prices.forEach(price => {
                            price_map.set(price.id, price);
                        })

                        let price = price_map.get(id);
                        let selling_price = price.price;
                        let origin_price = '';

                        if (price.promotion_price) {
                            selling_price = price.promotion_price;
                            origin_price = price.price;
                        }

                        let image = node.image.href;
                        let image_width = Graphite.default(node.image.width, '');
                        let image_height = Graphite.default(node.image.height, '');

                        if (price?.image?.href) {
                            image = price.image.href;    
                        }

                        item = {
                            "image": image,
                            "image_width": image_width,
                            "image_height": image_height,
                            "pid": id,
                            "id": price.id,
                            "title": node.title,
                            "sub_title": Graphite.default(node.sub_title, ''),
                            "sku": price.sku,
                            "qty": 1,
                            "price": selling_price,
                            "weight": price.weight,
                            "free-shipping": Graphite.default(node.free_shipping, 'off')
                        }

                        resolve(item);
                    }
                } else {
                    let price = node.prices[0];

                    if (!price) {
                        price = {
                            "pid": id,
                            "id": id + "-1ievao1u1",
                            "sku": "Lorem ipsum",
                            "price": 340,
                            "promotion_price": "",
                            "weight":"0.2",
                            "available":"on",
                            "image":{
                                "href":"",
                                "alt":""
                            }
                        }
                    }

                    let selling_price = price.price;

                    if (price.promotion_price) {
                        selling_price = price.promotion_price;
                    }

                    let image = node.image.href;
                    let image_width = Graphite.default(node.image.width, '');
                    let image_height = Graphite.default(node.image.height, '');

                    if (price?.image?.href) {
                        image = price.image.href;
                        image_width = price.image.width;
                        image_height = price.image.height; 
                    }

                    item = {
                        image: image,
                        image_width: image_width,
                        image_height: image_height,
                        pid: id,
                        id: node.id,
                        title: node.title,
                        sub_title: Graphite.default(node.sub_title, ''),
                        sku: Graphite.default(node.sub_title, ''),
                        qty: 1,
                        price: Graphite.default(selling_price, 300),
                        weight: price.weight,
                        free_shipping: Graphite.default(node.free_shipping, 'off')
                    }

                    resolve(item);
                }
            })
        })
    },

    track_event: (event, data) => {
        if ((typeof(gtag) === 'function') && Graphite.has_permission('#google-analyticts#')) {
            gtag('event', event, data);            
        }    

        if ((typeof(fbq) === 'function') && Graphite.has_permission('#facebook-pixel#')) {
            if (event === 'page_view') {
                let ts = Date.now();
                let it = ts - 861648;
                let pixel = fbq.getState().pixels[0];

                document.body.append('<img width="1" height="1" style="display:none" src="https://www.facebook.com/tr?id=' + pixel.id + '&ev=PageView&dl=' + encodeURIComponent(data.page_location) + '&sw=' + screen.width + '&sh=' + screen.height + '&ts=' + ts +'&it=' + it + '&v=' + fbq.version + '&r=stable&ec=0&o=30&cs_est=true&coo=false&rqm=GET&noscript=1" />');
            } else {
                fbq.push('trackCustom', event, data);                
            }
        }
    },

    send_mail: (data) => {
       return Graphite.post_json('./process/send-mail.php', data);
    },

    load_font: (font_name) => {
        if (Graphite.has_font(font_name)) return;

        let fonts = Object.keys(Graphite.config.fonts);

        if (!fonts.includes(font_name)) return;

        Graphite.config.fonts[font_name].forEach(font => {
            let options = {
                style: Graphite.default(font.style, 'normal'),
                weight: Graphite.default(font.weight, '400'),
                sizeAdjust: Graphite.default(font.size_adjust, '100%'),
                stretch: Graphite.default(font.stretch, 'normal'),
                unicodeRange: Graphite.default(font.unicode_range, 'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD')                
            }

            const new_font = new FontFace(font_name, 'url(' + font.src + ')', options);
            document.fonts.add(new_font);        
        })
    },

    has_font: (font_name) => {
        let fonts = new Array();

        document.fonts.forEach(font => {
            fonts.push(font.family);
        })

        return fonts.includes(font_name);
    },

    get_fonts: () => {
        let arr = [];

        document.fonts.forEach(font => {
            arr.push(font.family);
        })

        return arr;
    },

    scroll_into_view: (el, offset = 0) => {
        let dlg = Graphite.ModalDialog.get_active_dialog();

        if (dlg) {
            dlg.dialog.scroll(0, el.offsetTop - offset);            
        } else {
            window.scroll(0, el.offsetTop - offset);
        }
    },

    notify: (topic, msg) => {
        return fetch('https://ntfy.sh/' + topic, {
            method: 'POST', // PUT works too
            body: msg
        })        
    },

    set_cookie: (cName, cValue, domain = '', expDays = 30) => {
        let date = new Date();
        date.setTime(date.getTime() + (expDays * 24 * 60 * 60 * 1000));
        const expires = "expires=" + date.toUTCString();
        let cookie = cName + "=" + cValue + "; " + expires + "; path=/";

        if (domain) {
            cookie += '; domain=' + domain;
        }

        document.cookie = cookie;
    },


    read_cookie: name => {
        let nameEQ = name + "=";
        let ca = document.cookie.split(';');
        
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) == ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
        }

        return null;
    },

    switch_language: to => {
        let current_language = Graphite.get_google_translate_language();

        if (to == current_language) return;

        Graphite.set_cookie( 'googtrans', '/' + Graphite.config.default_language + '/' + to);
        Graphite.set_cookie( 'googtrans', '/' + Graphite.config.default_language + '/' + to, Graphite.config.domain);
        document.location.reload();
    },

    get_google_translate_language: () => {
        let value = Graphite.default(Graphite.read_cookie('googtrans'), '');

        if (value) {
            let arr = value.split('/');
            
            if (arr.length == 3) {
                return arr[2];
            }   
        }

        return '';
    },

    load_script: src => {
        return new Promise((resolve, reject) => {
            let script = document.createElement('script');

            script.onload = () => {
                resolve();
            }

            document.head.appendChild(script);
            script.src = src;
        })
    },

    setup_mx_placeholder: (el) => {
        let size = el.getAttribute('data-size');
        let max_width = Math.round(el.getAttribute('data-max-width') * Graphite.config.scale_factor);
        let default_aspect_ratio = el.getAttribute('data-default-aspect-ratio');
        let recommended_size = Graphite.default(el.getAttribute('data-recommended-size'), '');

        if (size == 'auto') {
            let el_img = el.querySelector('img');
            let href = el_img.getAttribute('data-href');
            let width = el_img.getAttribute('width');
            let height = el_img.getAttribute('height');

            el.setAttribute('data-max-width', max_width);

            if (href) {
                el.style.setProperty('--aspect-ratio', width + ' / ' + height);                
            } else {
                el.style.setProperty('--aspect-ratio', default_aspect_ratio);
            }

            if (recommended_size) {
                let el_recommended = el.querySelector('span.recommended-size');
                
                if (width && width <= 120) {
                    if (el_recommended) {
                        el_recommended.remove();
                    }
                } else {
                    if (height && height <= 120) {
                        if (el_recommended) {
                            el_recommended.remove();
                        }
                    } else {
                        if (!el_recommended) {
                            el_recommended = Graphite.el('<span class="recommended-size"></span>');
                            el.append(el_recommended);
                        }         

                        el_recommended.text(recommended_size);
                    }
                }
            }
        } else {
            let [width, height] = size.split('x');
            let max_height = Graphite.default(el.getAttribute('data-max-height'), '');

            el.style.setProperty('--aspect-ratio', width + ' / ' + height);
            el.setAttribute('data-max-width', max_width);

            if (max_height) {
                el.setAttribute('data-max-height', Math.round(max_height * Graphite.config.scale_factor))
            }

            let el_recommended = el.querySelector('span.recommended-size');

            if (width <= 120) {
                if (el_recommended) {
                    el_recommended.remove;
                }
            } else { 
                if (height <= 120) {
                    if (el_recommended) {
                        el_recommended.remove;
                    }                    
                } else {     
                    if (!el_recommended) {
                        el_recommended = Graphite.el('<span class="recommended-size"></span>');
                        el.append(el_recommended);
                    }         

                    el_recommended.text(width + 'x' + height);
                }
            }
        }
    },

    setup_video_mx_placeholder: (el) => {
        let el_video = el.querySelector('video');
        let default_aspect_ratio = el.getAttribute('data-default-aspect-ratio');
        let href = el_video.getAttribute('data-href');

        if (href) {
            el.style.setProperty('--aspect-ratio', el_video.getAttribute('width') + ' / ' + el_video.getAttribute('height'));
            el.style.setProperty('--max-width', el_video.getAttribute('width') + 'px');
        } else {
            el.style.setProperty('--aspect-ratio', default_aspect_ratio);            
            el.style.setProperty('--max-width', '100%');
        }
    },

    load_image: url => {
        return new Promise((resolve, reject) => {
            let el_img = document.createElement('img');
            let on_load, on_error;

            if (!url) {
                reject();
                return;
            }

            on_load = (evt) => {
                let el = evt.target;

                el.removeEventListener('load', on_load);
                el.removeEventListener('load', on_error);
                el.remove();

                resolve();
                return;
            }

            on_error = (evt) => {
                let el = evt.target;


                el.removeEventListener('load', on_load);
                el.removeEventListener('load', on_error);
                el.remove();

                reject();
                return;
            }
            
            el_img.addEventListener('load', on_load);
            el_img.addEventListener('error', on_error);
            
            el_img.src = url;
        })
    }
}

Graphite.app.state.theme = Graphite.default(localStorage.getItem('theme'), 'light');
Graphite.app.state.mode = Graphite.default(localStorage.getItem('mode'), 'live');

Graphite.state = new React({
    ready: false,
    ctrlKey: false,
    shiftKey: false,
    saving: false
})

Graphite.state.on('state-changed', (prop, current_state, prev_state) => {  
    if (Graphite.event_handlers.has('state-changed')) {
        let cb = Graphite.event_handlers.get('state-changed');

        cb(prop, current_state, prev_state);

        if (current_state.ready != prev_state.ready) {
            if (current_state.ready) {
                let app = document.querySelector('#app');

                if (app) {
                    app.ready();
                }

                document.trigger('app-ready');
            }
        }

        if (current_state.ctrlKey != prev_state.ctrlKey) {
            if (current_state.ctrlKey) {
                document.documentElement.classList.add('ctrl');
            } else {
                document.documentElement.classList.remove('ctrl');
            }            
        }

        if (current_state.shiftKey != prev_state.shiftKey) {
            if (current_state.shiftKey) {
                document.documentElement.classList.add('shift');
            } else {
                document.documentElement.classList.remove('shift');
            }            
        }
    }
})

Graphite.bc = new BroadcastChannel('graphite');

Graphite.bc.onmessage = (event) => {
    if (event.data.topic == 'state-changed') {
        let prev_state = Graphite.app.state;
        let current_state = event.data.state;

        function is_changed(key) {
            return current_state[key] != prev_state[key]; 
        }

        Graphite.app.state = current_state;

        if (is_changed('user')) {
            if (current_state.user) {
                Graphite.app.state.mode = 'edit';
                document.documentElement.setAttribute('data-mode', 'edit'); 
                document.documentElement.classList.add('login'); 
                document.trigger('login');                

                if (!prev_state.user) {
                    sessionStorage.setItem('reload-from-previous-session', true);
                    location.reload();
                }
            } else {
                Graphite.app.state.mode = 'live';
                document.documentElement.setAttribute('data-mode', 'live'); 
                document.documentElement.classList.remove('login'); 

                let admin_toolbar = document.querySelector('[data-role="admin-toolbar"]');

                if (admin_toolbar) {
                    admin_toolbar.fadeOut(600, () => {
                        admin_toolbar.remove();
                    })
                }

                document.trigger('logout');                
            }
        }

        if (is_changed('theme')) {
            document.documentElement.setAttribute('data-theme', current_state.theme); 
            document.trigger('theme-changed');                
        }

        if (is_changed('mode')) {
            document.documentElement.setAttribute('data-mode', current_state.mode); 
            document.trigger('mode-changed');
        }
    }
}

function x_components_autoload(options) {
    let prefix = 'mx';

    Graphite.x_components_observer = new IntersectionObserver((entries, observerRef) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const name = entry.target.nodeName.toLowerCase();
          observerRef.unobserve(entry.target);

          if (!customElements.get(name)  && !Graphite.import_queue.has(name)) {
            Graphite.import_component(name).finally(() => {
                Graphite.import_queue.delete(name);
            })
          }
        }
      })
    }, {
        rootMargin: '300px'
    })

    let on_app_ready = () => {
        let els = document.querySelectorAll('*');

        els.forEach(el => {            
            let tagName = el.tagName.toLowerCase();
            if (tagName.startsWith(prefix + '-')) {
                Graphite.x_components_observer.observe(el);
            }
        })
        
        document.removeEventListener('app-ready', on_app_ready);
    }

    document.addEventListener('app-ready', on_app_ready);
}

document.on('keydown', evt => {
    if (evt.keyCode == 17) {
        Graphite.state.set('ctrlKey', true);
    } else if (evt.keyCode == 16) {
        Graphite.state.set('shiftKey', true);
    } else if (evt.keyCode == 27) {
        if (Graphite.mobile_menu) {
            if (document.documentElement.classList.contains('show-mobile-menu')) {
                Graphite.mobile_menu.hide();
            }
        }
    } else if (evt.keyCode == 13) {
        let dlg = Graphite.ModalDialog.get_active_dialog();

        if (dlg) {
            let button = dlg.popup.querySelector('[data-default-button]');

            if (button) {
                button.click();
            }
        }        
    }
})

document.on('keyup', evt => {
    if (evt.keyCode == 17) {
        Graphite.state.set('ctrlKey', false);
    } else if (evt.keyCode == 16) {
        Graphite.state.set('shiftKey', false);
    }
})

x_components_autoload();
